
import { addDays } from 'date-fns/addDays';
import { endOfDay } from 'date-fns/endOfDay';
import { isBefore } from 'date-fns/isBefore';
import { startOfDay } from 'date-fns/startOfDay';
import { create } from 'zustand';
import ordersApi, { PaginatedHistoryParams } from '../api/order';
import { OrderHistoryItem } from '../common/types';

interface PollingOptions {
  initialInterval: number;
  maxInterval: number;
}


type OrdersStore = {
  hasLoaded: boolean;
  orders: OrderHistoryItem[];
  count: number;
  loading: boolean;
  error: unknown | null;
  params: PaginatedHistoryParams;
  updateParams: (params: Partial<PaginatedHistoryParams>) => void;
  resetFilters: () => void;
  fetchOrders: (params?: PaginatedHistoryParams, force?: boolean) => Promise<void>;
  startPolling: (options?: PollingOptions) => void;
  stopPolling: () => void;
};

const defaultPollingOptions: PollingOptions = {
  initialInterval: 2000,
  maxInterval: 15000,
};

let _ordersInterval: NodeJS.Timeout | null = null;

const getDefaultParams = () => {
  return {
    startDate: startOfDay(new Date()),
    endDate: addDays(startOfDay(new Date()), 1),
    skip: 0,
    take: 100,
    hidePaid: true,
    customerName: ''
  }
}

const usePOSOrders = create<OrdersStore>((set, get) => ({
  hasLoaded: false,
  hidePaid: true,
  orders: [],
  loading: false,
  error: null,
  count: 0,
  params: getDefaultParams(),

  resetFilters: () => {
    set({ params: getDefaultParams() });
    get().fetchOrders(get().params, true)
  },

  updateParams: (params: Partial<PaginatedHistoryParams>) => {
    const newParams = get().params
    if (params.startDate !== undefined) {
      newParams.startDate = params.startDate
      if (params.startDate >= newParams.endDate) {
        newParams.endDate = addDays(params.startDate, 1)
      }
    } else if (params.endDate !== undefined) {
      newParams.endDate = params.endDate
      if (params.endDate <= newParams.startDate) {
        newParams.startDate = addDays(params.endDate, -1)
      }
    }
    newParams.skip = params.skip !== undefined ? Math.abs(params.skip) : newParams.skip;
    newParams.take = params.take !== undefined ? Math.abs(params.take) : newParams.take;
    newParams.hidePaid = params.hidePaid !== undefined ? params.hidePaid : newParams.hidePaid;
    newParams.customerName = params.customerName !== undefined ? params.customerName : newParams.customerName;

    set({ params: newParams });
    get().fetchOrders(newParams, true);
  },

  fetchOrders: async (params?: PaginatedHistoryParams, force?: boolean) => {
    if (get().loading && !force) {
      return;
    }
    try {
      const requestParams = params || get().params

      if (!requestParams.endDate || isBefore(requestParams.endDate, requestParams.startDate)) {
        requestParams.endDate = endOfDay(requestParams.startDate);
      }
      if (!requestParams.skip || requestParams.skip < 0) {
        requestParams.skip = 0;
      }
      if (!requestParams.take || requestParams.take < 0) {
        requestParams.take = 100;

      }
      set({ loading: true, hasLoaded: true, params: requestParams });

      const result = await ordersApi.getOrderHistory(requestParams);

      if (result.data?.data) {
        set({ orders: result.data.data ?? [], count: result.data.count, loading: false, error: null });
      } else {
        set({ orders: [], count: 0, loading: false, error: new Error(`Failed to fetch orders: status ${result.status} ${result.statusText}`) });
      }

    } catch (err) {
      console.error(err);
      set({ orders: [], count: 0, loading: false, error: err });
    }
  },

  startPolling: (options = defaultPollingOptions) => {
    const { initialInterval, maxInterval } = options;
    const pollingInterval =
      initialInterval > maxInterval ? maxInterval : initialInterval;
    get().fetchOrders(get().params);
    if (_ordersInterval) {
      clearInterval(_ordersInterval);
    }
    _ordersInterval = setInterval(() => {
      get().fetchOrders(get().params);
    }, pollingInterval);
  },
  stopPolling: () => {
    if (_ordersInterval) {
      clearInterval(_ordersInterval);
    }
  },
}));

export default usePOSOrders;

if (!usePOSOrders.getState().hasLoaded) {
  usePOSOrders.getState().fetchOrders();
}
